/**
 * Caregiver profile data fetching hooks
 * Provides SWR-based hooks for fetching public caregiver profile data and availability
 */

import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { api } from '@/lib/api';
import type { ApiError } from '@/lib/api';
import type {
  PublicCareGiverProfileV2,
  PrivateCareGiverProfileV2,
  CareGiverProfile,
} from '@/types/profile';
import type { AvailabilitySlot } from '@/types/availability';
import { publicConfig, staticConfig } from '@/lib/swr-config';

// ============================================================================
// Types
// ============================================================================

interface AvailabilityResponse {
  slots: AvailabilitySlot[];
}

interface ProfilePhotoUploadResponse {
  profilePhotoUrl: string;
  profilePhotoVersion: string;
}

export interface CaregiverClientVerifyStartRequest {
  contactNumber: string;
}

export interface CaregiverClientVerifyStartResponse {
  status: string;
}

export interface CaregiverClientVerifyCompleteOtpRequest {
  contactNumber: string;
  code: string;
  inviteCode?: never;
}

export interface CaregiverClientVerifyCompleteInviteRequest {
  inviteCode: string;
  contactNumber?: never;
  code?: never;
}

export type CaregiverClientVerifyCompleteRequest =
  | CaregiverClientVerifyCompleteOtpRequest
  | CaregiverClientVerifyCompleteInviteRequest;

export interface CaregiverClientVerifyCompleteResponse {
  displayIdentity: string;
}

export interface CaregiverProfileAccessRequest {
  fullName: string;
  email: string;
  phoneNumber: string;
  message: string;
}

export interface CaregiverProfileAccessResponse {
  status: 'ok' | 'already_has_access';
}

const ignoreAccessDeniedOnError: NonNullable<typeof staticConfig.onError> = (
  error,
  key,
  config
): void => {
  const apiError = error as ApiError;
  if (apiError.statusCode === 401 || apiError.statusCode === 403 || apiError.statusCode === 404) {
    return;
  }

  staticConfig.onError?.(error, key, config);
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch caregiver public profile via v2 endpoint.
 *
 * @param linkPath - The unique link code for the caregiver profile
 * @returns SWR response for /api/v2/profiles/{linkPath}/public
 */
export function useCaregiverPublicProfileV2(linkPath: string) {
  const key = linkPath ? `/api/v2/profiles/${linkPath}/public` : null;

  return useSWR<PublicCareGiverProfileV2>(
    key,
    url =>
      api.get<PublicCareGiverProfileV2>(url, {
        requiresAuth: false,
      }),
    {
      ...publicConfig,
      shouldRetryOnError: error => error?.statusCode !== 404 && error?.statusCode !== 403,
    }
  );
}

/**
 * Fetch caregiver private profile via v2 endpoint.
 *
 * This endpoint is protected by the client invite session cookie.
 * `credentials: 'include'` is required so the browser sends that cookie.
 *
 * @param linkPath - The unique link code for the caregiver profile
 * @returns SWR response for /api/v2/profiles/{linkPath}/private
 */
export function useCaregiverPrivateProfileV2(linkPath: string, enabled = true) {
  const key = linkPath && enabled ? `/api/v2/profiles/${linkPath}/private` : null;

  return useSWR<PrivateCareGiverProfileV2>(
    key,
    url =>
      api.get<PrivateCareGiverProfileV2>(url, {
        requiresAuth: false,
        credentials: 'include',
      }),
    {
      ...staticConfig,
      revalidateOnReconnect: true,
      errorRetryCount: 2,
      onError: ignoreAccessDeniedOnError,
      shouldRetryOnError: error =>
        error?.statusCode !== 401 && error?.statusCode !== 403 && error?.statusCode !== 404,
    }
  );
}

/**
 * Client OTP verification flow for private profile access.
 *
 * Both endpoints are unauthenticated, but require cookie credentials so the browser can receive/send the client session cookie.
 *
 * @param linkPath - The unique link code for the caregiver profile
 * @returns trigger functions for verify/start and verify/complete
 */
export function useCaregiverClientVerificationV2(
  linkPath: string,
  options?: { onError?: (err: any, key: string, config: any) => void }
) {
  const verifyStartPath = linkPath ? `/api/v2/profiles/${linkPath}/clients/verify/start` : null;
  const verifyCompletePath = linkPath
    ? `/api/v2/profiles/${linkPath}/clients/verify/complete`
    : null;

  const { trigger: startVerification, isMutating: isStartingVerification } = useSWRMutation(
    verifyStartPath,
    async (
      path: string,
      { arg }: { arg: CaregiverClientVerifyStartRequest }
    ): Promise<CaregiverClientVerifyStartResponse> => {
      return api.post<CaregiverClientVerifyStartResponse>(path, arg, {
        requiresAuth: false,
        credentials: 'include',
      });
    }
  );

  const { trigger: completeVerification, isMutating: isCompletingVerification } = useSWRMutation(
    verifyCompletePath,
    async (
      path: string,
      { arg }: { arg: CaregiverClientVerifyCompleteRequest }
    ): Promise<CaregiverClientVerifyCompleteResponse> => {
      return api.post<CaregiverClientVerifyCompleteResponse>(path, arg, {
        requiresAuth: false,
        credentials: 'include',
      });
    },
    options
  );

  return {
    startVerification,
    completeVerification,
    isStartingVerification,
    isCompletingVerification,
  };
}

export function useCaregiverProfileAccessRequestV2(linkPath: string) {
  const accessRequestPath = linkPath ? `/api/v2/profiles/${linkPath}/access-requests` : null;

  const { trigger: submitAccessRequest, isMutating: isSubmittingAccessRequest } = useSWRMutation(
    accessRequestPath,
    async (
      path: string,
      { arg }: { arg: CaregiverProfileAccessRequest }
    ): Promise<CaregiverProfileAccessResponse> => {
      return api.post<CaregiverProfileAccessResponse>(path, arg, {
        requiresAuth: false,
      });
    }
  );

  return {
    submitAccessRequest,
    isSubmittingAccessRequest,
  };
}

/**
 * Fetch caregiver public availability via v2 endpoint.
 *
 * @param linkPath - The unique link code for the caregiver profile
 * @returns SWR response for /api/v2/profiles/{linkPath}/public/availability-slots
 */
export function useCaregiverAvailability(linkPath: string) {
  const key = linkPath ? `/api/v2/profiles/${linkPath}/public/availability-slots` : null;

  return useSWR<AvailabilityResponse>(
    key,
    url =>
      api.get<AvailabilityResponse>(url, {
        requiresAuth: false,
      }),
    {
      ...publicConfig,
      shouldRetryOnError: error => error?.statusCode !== 404 && error?.statusCode !== 403,
    }
  );
}

/**
 * Fetch caregiver private availability via v2 endpoint.
 *
 * This endpoint is protected by the client invite session cookie.
 * `credentials: 'include'` is required so the browser sends that cookie.
 *
 * @param linkPath - The unique link code for the caregiver profile
 * @returns SWR response for /api/v2/profiles/{linkPath}/private/availability-slots
 */
export function useCaregiverPrivateAvailabilityV2(linkPath: string, enabled = true) {
  const key =
    linkPath && enabled ? `/api/v2/profiles/${linkPath}/private/availability-slots` : null;

  const swr = useSWR<AvailabilityResponse>(
    key,
    url =>
      api.get<AvailabilityResponse>(url, {
        requiresAuth: false,
        credentials: 'include',
      }),
    {
      ...staticConfig,
      revalidateOnReconnect: true,
      onError: ignoreAccessDeniedOnError,
      shouldRetryOnError: error =>
        error?.statusCode !== 401 && error?.statusCode !== 403 && error?.statusCode !== 404,
      errorRetryCount: 2,
      dedupingInterval: 2 * 60 * 1000,
      refreshInterval: 5 * 60 * 1000,
    }
  );

  return {
    ...swr,
    refetch: () => swr.mutate(),
  };
}

// ============================================================================
// Authenticated Caregiver Profile Hooks
// ============================================================================

/**
 * Fetch the authenticated caregiver's own profile
 * Used in dashboard settings for editing profile
 *
 * @returns SWR response with profile data, loading state, error handling, and update function
 */
export function useMyProfile(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { data, error, isLoading, isValidating, mutate } = useSWR<CareGiverProfile>(
    enabled ? '/api/v1/me/care-giver-profile' : null,
    url => api.get(url),
    {
      ...staticConfig,
      revalidateOnFocus: false,
    }
  );

  // Update profile mutation
  const { trigger: updateProfile, isMutating: isUpdating } = useSWRMutation(
    '/api/v1/me/care-giver-profile',
    async (url: string, { arg }: { arg: Partial<CareGiverProfile> }) => {
      const response = await api.patch<CareGiverProfile>(url, arg);
      return response;
    },
    {
      onSuccess: data => {
        mutate(data, false);
      },
    }
  );

  const { trigger: uploadProfilePhoto, isMutating: isUploadingProfilePhoto } = useSWRMutation(
    '/api/v1/me/care-giver-profile/photo',
    async (url: string, { arg }: { arg: File }) => {
      const formData = new FormData();
      formData.append('file', arg);

      return api.request<ProfilePhotoUploadResponse>(url, {
        method: 'POST',
        body: formData,
      });
    },
    {
      onSuccess: result => {
        mutate(
          current =>
            current
              ? {
                  ...current,
                  profilePhotoUrl: result.profilePhotoUrl,
                  profilePhotoVersion: result.profilePhotoVersion,
                }
              : current,
          false
        );
      },
    }
  );

  return {
    profile: data,
    error,
    isLoading,
    isValidating,
    isUpdating,
    updateProfile,
    isUploadingProfilePhoto,
    uploadProfilePhoto,
    mutate,
  };
}
